// ===== World 14: Granny's attic – MAGNETS =====
// A magnet pulls a steel ball (and granny's iron), but not wooden or glass balls.
// The magnet works at a distance and through things; the closer, the stronger. When the steel ball touches the magnet, it goes CLICK.
// Hero: steel ball -> goal: music box (bell) or granny's biscuit tin (bowl).
import { defineWorld, definePart, defineObj } from '../../core/registry';
import { lg, DEG, C } from '../../core/draw-helpers';
import type { Ball, Ctx2D, GameBody, Gfx, GoalState, ObjData, PhysicsApi, SoundFn, WorldDef, WorldMeta } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K } from './shared';

// ---------- constants ----------
const INK = C.ink;

// ---------- magnet physics ----------
/** A magnet: pole point (x, y), pole direction (nx, ny), strength S, max acceleration, range, catch distance, on, hold (frames before release, 0 = never) */
export interface Magnet {
  x: number; y: number; nx: number; ny: number;
  S: number; amax: number; range: number; catchD: number;
  on: boolean; hold: number; held: Held[]; snap: boolean; offT: number;
  release?: boolean;
}
interface Held { b: GameBody; t: number }
declare module '../../core/types' {
  interface SimState { atticMags?: Magnet[] }
  interface GameBody { atticIron?: boolean; atticFrozen?: boolean; atticHeldBy?: Magnet | null }
}

const MAT = (b: GameBody) => (b.def && b.def.skin) || 'steel';
const isIron = (api: PhysicsApi, b: GameBody) => b && (b.atticIron || (api.isBall(b) && MAT(b) === 'steel'));
function mags(api: PhysicsApi) { return api.S.atticMags || (api.S.atticMags = []); }
function magStep(api: PhysicsApi, m: Magnet) {
  const { Body } = api;
  // held things: count down, release
  for (const h of m.held) {
    if (h.b.atticFrozen) { if (m.hold && ++h.t >= m.hold) m.release = true; continue; }
    // catch phase: brake softly, pull towards the pole, freeze once it lies still
    h.t++;
    const v = h.b.velocity;
    Body.setVelocity(h.b, { x: v.x * 0.72, y: v.y * 0.72 });
    const dx = m.x - h.b.position.x, dy = m.y - h.b.position.y, d = Math.hypot(dx, dy) || 1;
    Body.applyForce(h.b, h.b.position, { x: dx / d * h.b.mass * 1.4 / 277.8, y: dy / d * h.b.mass * 1.4 / 277.8 });
    if ((h.b.speed < 0.45 && h.t > 4) || h.t > 30) {
      if (m.snap) { const r = h.b.circleRadius || 18; Body.setPosition(h.b, { x: m.x - dx / d * (r + 1), y: m.y - dy / d * (r + 1) }); }
      Body.setStatic(h.b, true); h.b.atticFrozen = true; h.t = 0;
    }
  }
  if (m.release) { m.release = false; letGo(api, m); return; }
  if (!m.on) return;
  for (const b of api.S.dyn) {
    if (b.isStatic || b.atticHeldBy || !isIron(api, b)) continue;
    const dx = m.x - b.position.x, dy = m.y - b.position.y, d = Math.hypot(dx, dy);
    if (d > m.range) continue;
    if (d < m.catchD + (b.circleRadius || 18)) {           // CLICK
      b.atticHeldBy = m; m.held.push({ b, t: 0 });
      api.sfx('attic_click', b.position.x, b.position.y, '#FFE07A', 8);
      continue;
    }
    const cos = -(dx * m.nx + dy * m.ny) / (d || 1);          // 1 = the ball is in front of the pole
    const dir = Math.max(0.35, 0.68 + 0.32 * cos);
    const edge = Math.min(1, (m.range - d) / 50);
    const a = Math.min(m.amax, m.S / (d * d)) * dir * edge;   // px/frame²  (gravity = 0.278)
    const k = b.mass * a / 277.8;
    Body.applyForce(b, b.position, { x: dx / d * k, y: dy / d * k });
  }
}
function letGo(api: PhysicsApi, m: Magnet) {
  for (const h of m.held) {
    const b = h.b;
    if (b.atticFrozen) { api.Body.setStatic(b, false); b.atticFrozen = false; }
    api.Body.setVelocity(b, { x: 0, y: 0 }); api.Body.setAngularVelocity(b, 0);
    b.atticHeldBy = null;
  }
  m.held = []; m.on = false; m.offT = api.S.t;
  api.sfx('attic_release', m.x, m.y);
}
function newMag(api: PhysicsApi, x: number, y: number, a: number, o?: Partial<Magnet>) {
  const m: Magnet = Object.assign({ x, y, nx: -Math.sin(a), ny: Math.cos(a), S: 4300, amax: 1.0, range: 280, catchD: 10, on: true, hold: 0, held: [], snap: true, offT: -1 }, o || {});
  mags(api).push(m);
  return m;
}
function magBusy(m: Magnet) { return m.held.some(h => !h.b.atticFrozen || m.hold); }

// ---------- drawing helpers ----------
// Horseshoe magnet in local coordinates: the opening points towards +y, the poles end at y = 26
function horseshoe(ctx: Ctx2D, s?: number) {
  s = s || 1;
  ctx.save(); ctx.scale(s, s);
  ctx.lineCap = 'butt'; ctx.lineJoin = 'round';
  const path = () => { ctx.beginPath(); ctx.moveTo(-17, 14); ctx.lineTo(-17, -2); ctx.arc(0, -2, 17, Math.PI, 0); ctx.lineTo(17, 14); };
  path(); ctx.strokeStyle = INK; ctx.lineWidth = 24; ctx.stroke();
  path(); ctx.strokeStyle = '#E0394A'; ctx.lineWidth = 18; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, -2, 17, Math.PI * 1.1, Math.PI * 1.55); ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 5; ctx.lineCap = 'round'; ctx.stroke();
  for (const sx of [-1, 1]) {
    ctx.beginPath(); ctx.rect(sx * 17 - 9, 12, 18, 14);
    ctx.fillStyle = lg(ctx, 12, 26, '#F4F6FA', '#A8B0C2'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  }
  ctx.restore();
}
// Soft magnetic arcs in front of the pole (only while the magnet is on)
function field(ctx: Ctx2D, m: Pick<Magnet, 'x' | 'y' | 'nx' | 'ny' | 'range'>, t: number, alpha: number) {
  if (alpha <= 0.01) return;
  const ang = Math.atan2(m.ny, m.nx);
  ctx.save(); ctx.lineCap = 'round';
  for (let i = 0; i < 3; i++) {
    const f = ((t * 0.012 + i / 3) % 1), rad = 22 + (1 - f) * (m.range * 0.62);
    const al = alpha * 0.55 * Math.sin(f * Math.PI);
    ctx.strokeStyle = `rgba(230,70,110,${al.toFixed(3)})`; ctx.lineWidth = 5;
    ctx.beginPath(); ctx.arc(m.x, m.y, rad, ang - 0.9, ang + 0.9); ctx.stroke();
  }
  ctx.restore();
}
function sparkle(ctx: Ctx2D, x: number, y: number, t: number, k: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(t * 0.05);
  ctx.fillStyle = `rgba(255,224,122,${k})`;
  ctx.beginPath(); for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4, r = i % 2 ? 4 : 11; ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); } ctx.fill();
  ctx.restore();
}
// differs from draw-helpers selBox: centred on (0, 0), always dashed, radius 14 and ctx.roundRect (not rr)
function selBox(ctx: Ctx2D, w: number, h: number) { ctx.save(); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(-w / 2, -h / 2, w, h, 14); else ctx.rect(-w / 2, -h / 2, w, h); ctx.stroke(); ctx.restore(); }
// Reach in the build phase (faint dotted circle)
function reach(ctx: Ctx2D, x: number, y: number, r: number) { ctx.save(); ctx.setLineDash([3, 12]); ctx.lineCap = 'round'; ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(230,70,110,.28)'; ctx.beginPath(); ctx.arc(x, y, r, 0, 7); ctx.stroke(); ctx.restore(); }

// Egg timer (on the timer magnet), k = how much time has passed (0..1)
function eggTimer(ctx: Ctx2D, x: number, y: number, k: number, ring: number) {
  ctx.save(); ctx.translate(x, y); if (ring) ctx.rotate(Math.sin(ring * 1.3) * 0.25);
  ctx.beginPath(); ctx.ellipse(0, 0, 15, 13, 0, 0, 7); ctx.fillStyle = lg(ctx, -13, 13, '#FFF7D6', '#FFD36B'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(0, 0, 9, 8, 0, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  if (k > 0) { ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, 8, -Math.PI / 2, -Math.PI / 2 + k * Math.PI * 2); ctx.closePath(); ctx.fillStyle = '#FF8FA3'; ctx.fill(); }
  ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(Math.sin(k * 6.28) * 7, -Math.cos(k * 6.28) * 7); ctx.stroke();
  ctx.fillStyle = INK; ctx.beginPath(); ctx.rect(-4, -18, 8, 5); ctx.fill();
  ctx.restore();
}

// Balls: steel (hero), wood and glass.
// Differs from draw-helpers drawBall/face: material gradients and patterns, eye outline, dizzy eyes, held = scared face.
function drawBall(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!, mat = MAT(b);
  const v = b.velocity || { x: 0, y: 0 }, sp = Math.hypot(v.x, v.y);
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7);
  let gr;
  if (mat === 'wood') { gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05); gr.addColorStop(0, '#F9CE8C'); gr.addColorStop(0.6, '#D98E4A'); gr.addColorStop(1, '#A8612A'); }
  else if (mat === 'glass') { gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.05); gr.addColorStop(0, 'rgba(235,250,255,.95)'); gr.addColorStop(0.6, 'rgba(140,215,245,.85)'); gr.addColorStop(1, 'rgba(70,160,215,.9)'); }
  else { gr = ctx.createRadialGradient(-r * .35, -r * .45, r * .05, 0, 0, r * 1.05); gr.addColorStop(0, '#FFFFFF'); gr.addColorStop(0.45, '#C9D0DE'); gr.addColorStop(1, '#6F7890'); }
  ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.save(); ctx.rotate(b.roll || 0);
  // material pattern (rolls with the ball)
  if (mat === 'wood') {
    ctx.strokeStyle = 'rgba(120,60,20,.45)'; ctx.lineWidth = 1.6;
    for (const k of [0.45, 0.75]) { ctx.beginPath(); ctx.ellipse(r * 0.1, r * 0.55, r * k, r * k * 0.45, 0.2, Math.PI * 1.05, Math.PI * 1.95); ctx.stroke(); }
  } else if (mat === 'glass') {
    ctx.strokeStyle = 'rgba(255,120,170,.75)'; ctx.lineWidth = 3; ctx.lineCap = 'round';
    ctx.beginPath(); ctx.moveTo(-r * .7, r * .55); ctx.quadraticCurveTo(0, r * .1, r * .72, r * .5); ctx.stroke();
  } else {
    ctx.strokeStyle = 'rgba(255,255,255,.5)'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(0, 0, r * 0.82, 0.3, 1.2); ctx.stroke();
  }
  // face
  const lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
  const scared = v.y > 9, fast = sp > 8.5, held = b.atticHeldBy;
  const ex = r * 0.34, ey = -r * 0.12, er = r * 0.26;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 1.2; ctx.strokeStyle = 'rgba(59,42,92,.5)'; ctx.stroke();
    if ((b.dizzy || 0) > 0) { ctx.strokeStyle = INK; ctx.lineWidth = 1.6; ctx.beginPath(); for (let i = 0; i < 12; i++) { const tt = i / 12 * Math.PI * 3 + t * 0.3, r2 = i / 12 * er * 0.8; ctx.lineTo(s * ex + Math.cos(tt) * r2, ey + Math.sin(tt) * r2); } ctx.stroke(); }
    else { ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared || held ? 0.36 : 0.55), 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(0, r * 0.62, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared || held) { ctx.beginPath(); ctx.ellipse(0, r * 0.4, r * 0.15, r * 0.2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.25, r * 0.28, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,150,.45)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.6, r * 0.22, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .46, r * .26, r * .16, -0.6, 0, 7); ctx.fillStyle = mat === 'steel' ? 'rgba(255,255,255,.95)' : 'rgba(255,255,255,.6)'; ctx.fill();
  ctx.restore();
}

// Background things: block in the static body's size
function woodBlock(g: Gfx, ctx: Ctx2D, b: GameBody, c1: string, c2: string) { g.block(ctx, b.position.x, b.position.y, b.dw!, b.dh!, b.angle, c1, c2, Math.min(8, b.dh! / 2)); }

// Granny's iron
function iron(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  // handle
  ctx.lineWidth = 7; ctx.strokeStyle = INK; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(-18, -16); ctx.quadraticCurveTo(-14, -34, 6, -34); ctx.lineTo(20, -34); ctx.stroke();
  ctx.lineWidth = 4; ctx.strokeStyle = '#2B2B3A'; ctx.stroke();
  // sole plate
  ctx.beginPath(); ctx.moveTo(-35, 17); ctx.lineTo(35, 17); ctx.quadraticCurveTo(34, -6, 12, -17); ctx.lineTo(-30, -17); ctx.quadraticCurveTo(-36, -17, -35, -8); ctx.closePath();
  ctx.fillStyle = lg(ctx, -17, 17, '#8F97AA', '#4B5266'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.35)'; ctx.fillRect(-28, -13, 40, 4);
  ctx.fillStyle = '#C9D0DE'; ctx.fillRect(-34, 10, 68, 6);
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.strokeRect(-42, -42, 84, 66); }
  ctx.restore();
}
// Cotton reel (the seesaw's pivot)
function spool(ctx: Ctx2D, x: number, y: number) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#C98B5A'; ctx.strokeStyle = INK; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.rect(-22, 34, 44, 8); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.rect(-16, 8, 32, 26); ctx.fillStyle = '#6FB6E8'; ctx.fill(); ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,.45)'; ctx.lineWidth = 2;
  for (let k = 13; k < 34; k += 5) { ctx.beginPath(); ctx.moveTo(-15, k); ctx.lineTo(15, k + 2); ctx.stroke(); }
  ctx.fillStyle = '#C98B5A'; ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.rect(-22, 2, 44, 8); ctx.fill(); ctx.stroke();
  ctx.restore();
}
// Pincushion (tomato) with pins – bounces the ball up
function pincushion(ctx: Ctx2D, x: number, y: number, a: number, w: number, sq: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  const h = 26 - sq * 6, hw = w / 2;
  ctx.beginPath(); ctx.ellipse(0, 4 - h / 2 + 9, hw, h / 2 + 4, 0, 0, 7);
  ctx.fillStyle = lg(ctx, -12, 18, '#FF7B7B', '#D8434F'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.strokeStyle = 'rgba(150,30,40,.45)'; ctx.lineWidth = 2;
  for (const k of [-0.5, 0, 0.5]) { ctx.beginPath(); ctx.moveTo(k * hw, -h / 2 + 6); ctx.quadraticCurveTo(k * hw * 1.3, 6, k * hw * 0.9, h / 2 + 8); ctx.stroke(); }
  ctx.fillStyle = '#7BC96F'; ctx.beginPath(); for (let i = 0; i < 10; i++) { const t = i / 10 * Math.PI * 2, r = i % 2 ? 4 : 10; ctx.lineTo(Math.cos(t) * r, -h / 2 + 6 + Math.sin(t) * r * 0.45); } ctx.fill();
  const pins: [number, string][] = [[-hw * 0.6, '#FFC93C'], [hw * 0.55, '#6FB6E8']];
  for (const [px, c] of pins) {
    ctx.strokeStyle = '#9AA3B5'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(px, -h / 2 + 8); ctx.lineTo(px * 1.1, -h / 2 - 4); ctx.stroke();
    ctx.beginPath(); ctx.arc(px * 1.1, -h / 2 - 6, 4, 0, 7); ctx.fillStyle = c; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = INK; ctx.stroke();
  }
  ctx.beginPath(); ctx.ellipse(-hw * 0.45, -h / 2 + 10, 10, 3, -0.2, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.5)'; ctx.fill();
  if (sel) { ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.strokeRect(-hw - 6, -h / 2 - 14, w + 12, h + 30); }
  ctx.restore();
}
// Sandbag (the kit's stone: heavy, but NOT iron)
function sandbag(ctx: Ctx2D, x: number, y: number, a: number, sel: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  ctx.beginPath(); ctx.moveTo(-22, 20); ctx.quadraticCurveTo(-28, -6, -12, -18); ctx.lineTo(12, -18); ctx.quadraticCurveTo(28, -6, 22, 20); ctx.closePath();
  ctx.fillStyle = lg(ctx, -18, 20, '#EFD9A8', '#C9A66A'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-10, -18); ctx.lineTo(-6, -26); ctx.lineTo(6, -26); ctx.lineTo(10, -18); ctx.fillStyle = '#C9A66A'; ctx.fill(); ctx.stroke();
  ctx.strokeStyle = '#E0394A'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(-10, -19); ctx.lineTo(10, -19); ctx.stroke();
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 32, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 4; ctx.stroke(); }
  ctx.restore();
}
// Toolbox icon of the ruler (the kit's plank)
function rulerIcon(ctx: Ctx2D, g: Gfx) {
  g.block(ctx, 0, 0, 100, 16, -0.35, '#FFE58A', '#E9B53A', 5);
}

// ---------- skins ----------
const skins = {
  // beam / shelf of old wood
  attic_shelf(ctx: Ctx2D, b: GameBody, g: Gfx) {
    woodBlock(g, ctx, b, '#D9A46E', '#9C6F48');
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(110,60,25,.35)'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(-b.dw! / 2 + 12, 1); ctx.bezierCurveTo(-b.dw! / 6, -3, b.dw! / 6, 5, b.dw! / 2 - 12, 0); ctx.stroke();
    ctx.fillStyle = 'rgba(80,50,30,.55)';
    for (const px of [-b.dw! / 2 + 10, b.dw! / 2 - 10]) { ctx.beginPath(); ctx.arc(px, 0, 2.6, 0, 7); ctx.fill(); }
    ctx.restore();
  },
  // thin board / book (the magnet works through it)
  attic_book(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const w = b.dw!, h = b.dh!, vert = h > w;
    g.block(ctx, b.position.x, b.position.y, w, h, b.angle, '#7FB7E8', '#3F7FC0', 5);
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = '#FFF4DC';
    if (vert) ctx.fillRect(w / 2 - 6, -h / 2 + 5, 3, h - 10); else ctx.fillRect(-w / 2 + 5, h / 2 - 6, w - 10, 3);
    ctx.fillStyle = '#FFC93C';
    if (vert) { ctx.fillRect(-w / 2 + 3, -h / 4, w - 10, 4); ctx.fillRect(-w / 2 + 3, h / 4, w - 10, 4); } else { ctx.fillRect(-w / 4, -h / 2 + 3, 4, h - 10); ctx.fillRect(w / 4, -h / 2 + 3, 4, h - 10); }
    ctx.restore();
  },
  // sewing basket (wicker)
  attic_basket(ctx: Ctx2D, b: GameBody, g: Gfx) {
    woodBlock(g, ctx, b, '#F2C46B', '#C98B3A');
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(120,70,20,.45)'; ctx.lineWidth = 2;
    for (let y = -b.dh! / 2 + 8; y < b.dh! / 2 - 4; y += 9) { ctx.beginPath(); ctx.moveTo(-b.dw! / 2 + 3, y); ctx.lineTo(b.dw! / 2 - 3, y + 4); ctx.stroke(); }
    ctx.restore();
  },
  // cardboard box
  attic_cardboard(ctx: Ctx2D, b: GameBody, g: Gfx) {
    woodBlock(g, ctx, b, '#EBC78E', '#C9985A');
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(200,150,90,.8)'; ctx.fillRect(-b.dw! / 2 + 4, -b.dh! / 2 + Math.min(18, b.dh! * 0.25), b.dw! - 8, 5);
    ctx.fillStyle = 'rgba(160,110,60,.5)'; ctx.fillRect(-6, -b.dh! / 2 + 3, 12, Math.min(18, b.dh! * 0.25));
    ctx.restore();
  },
  // suitcase
  attic_suitcase(ctx: Ctx2D, b: GameBody, g: Gfx) {
    woodBlock(g, ctx, b, '#E8716B', '#B84A4A');
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.fillStyle = '#9C6F48';
    for (const sx of [-1, 1]) ctx.fillRect(sx * b.dw! * 0.3 - 5, -b.dh! / 2 + 2, 10, b.dh! - 2);
    ctx.fillStyle = '#FFC93C'; ctx.fillRect(-8, -b.dh! / 2 + 4, 16, 10);
    ctx.restore();
  },
  // floorboards
  attic_floor(ctx: Ctx2D, b: GameBody, g: Gfx) {
    woodBlock(g, ctx, b, '#C99A6B', '#A87C52');
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(90,55,25,.3)'; ctx.lineWidth = 3;
    for (let x = -b.dw! / 2 + 90; x < b.dw! / 2 - 20; x += 150) { ctx.beginPath(); ctx.moveTo(x, -b.dh! / 2 + 3); ctx.lineTo(x, b.dh! / 2); ctx.stroke(); }
    ctx.restore();
  },
};

function bgExtra(ctx: Ctx2D, W: number, H: number) {
  const v = ctx.createRadialGradient(W / 2, H / 2, 260, W / 2, H / 2, 780);
  v.addColorStop(0, 'rgba(255,250,240,0)'); v.addColorStop(1, 'rgba(120,80,40,.14)');
  ctx.fillStyle = v; ctx.fillRect(0, 0, W, H);
}

const goal = {
  // Granny's blue biscuit tin
  bowl(ctx: Ctx2D, gl: { x: number; y: number; w: number }, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl, top = y - 58;
    ctx.save();
    g.shadowOn(ctx, 14, 7);
    g.rr(ctx, x - w / 2 - 4, top, w + 8, 66, 12); ctx.fillStyle = lg(ctx, top, y + 8, '#5B8FE0', '#2F5FB0'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    // pattern
    ctx.strokeStyle = '#FFE9A8'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x - w / 2 + 2, top + 18); ctx.lineTo(x + w / 2 - 2, top + 18); ctx.moveTo(x - w / 2 + 2, y - 4); ctx.lineTo(x + w / 2 - 2, y - 4); ctx.stroke();
    ctx.fillStyle = '#FFE9A8';
    for (let i = -2; i <= 2; i++) { const px = x + i * w / 5.5; ctx.beginPath(); for (let k = 0; k < 10; k++) { const a = -Math.PI / 2 + k * Math.PI / 5, r = k % 2 ? 3 : 7; ctx.lineTo(px + Math.cos(a) * r, top + 36 + Math.sin(a) * r); } ctx.fill(); }
    // biscuits inside the tin
    ctx.beginPath(); ctx.ellipse(x, top, w / 2 + 4, 9, 0, 0, 7); ctx.fillStyle = '#20407A'; ctx.fill();
    for (let i = -1; i <= 1; i++) { ctx.beginPath(); ctx.ellipse(x + i * w * 0.24, top + 1, 14, 5, 0, Math.PI, 0); ctx.fillStyle = '#F2C27A'; ctx.fill(); }
    // lid leaning against the tin
    ctx.save(); ctx.translate(x + w / 2 + 6, top + 10); ctx.rotate(0.35);
    g.rr(ctx, -4, -30, 10, 60, 5); ctx.fillStyle = '#4A7FD0'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
    ctx.restore();
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: { x: number; y: number; w: number }) {
    const { x, y, w } = gl, top = y - 58;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, top, w / 2 + 4, 9, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#8FB7F0'; ctx.stroke();
    ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.beginPath(); ctx.ellipse(x, top, w / 2 + 6, 11, 0, 0, Math.PI); ctx.stroke(); ctx.restore();
  },
  // Music box (hit = won)
  bell(ctx: Ctx2D, gl: Pick<GoalState, 'x' | 'y' | 'hitT'>, t: number, g: Gfx) {
    const hit = gl.hitT != null, k = hit ? Math.min(1, (t - gl.hitT!) / 20) : 0;
    ctx.save(); ctx.translate(gl.x, gl.y + 14);
    g.shadowOn(ctx, 12, 6);
    g.rr(ctx, -36, -10, 72, 40, 9); ctx.fillStyle = lg(ctx, -10, 30, '#FF9FB6', '#E0607F'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.fillStyle = '#FFC93C'; ctx.fillRect(-36, 2, 72, 5);
    // crank
    ctx.save(); ctx.translate(40, 10); ctx.rotate(hit ? t * 0.3 : 0); ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -10); ctx.lineTo(7, -10); ctx.stroke(); ctx.restore();
    // lid (opens when the ball arrives)
    ctx.save(); ctx.translate(-36, -10); ctx.rotate(-k * 1.1);
    g.rr(ctx, 0, -10, 72, 12, 6); ctx.fillStyle = '#FFB3C7'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.restore();
    // little dancer
    const dy = -10 - k * 26 - 4;
    ctx.save(); ctx.translate(0, dy); ctx.rotate(hit ? Math.sin(t * 0.2) * 0.3 : 0);
    ctx.fillStyle = '#FFE07A'; ctx.beginPath(); ctx.moveTo(-9, 0); ctx.lineTo(9, 0); ctx.lineTo(0, -12); ctx.closePath(); ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -16, 5, 0, 7); ctx.fillStyle = '#FFE0C9'; ctx.fill(); ctx.stroke();
    ctx.restore();
    ctx.restore();
  },
};

// ---------- parts ----------
const parts = {
  // Horseshoe magnet: pulls steel (at a distance, through things). When the ball touches it, it goes CLICK – and the ball stays stuck.
  attic_magnet: definePart({
    rects: [{ x: 0, y: 0, w: 58, h: 54 }], rot: 45, defaultA: 0,
    draw(ctx, pl, sel) {
      const a = (pl.a || 0) * DEG, px = pl.x - Math.sin(a) * 27, py = pl.y + Math.cos(a) * 27;
      field(ctx, { x: px, y: py, nx: -Math.sin(a), ny: Math.cos(a), range: 280 }, performance.now() / 16, 0.8);
      if (sel) reach(ctx, px, py, 280);
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a);
      horseshoe(ctx); if (sel) selBox(ctx, 74, 70);
      ctx.restore();
    },
    icon(ctx) { ctx.save(); ctx.rotate(0.5); horseshoe(ctx, 1.25); ctx.restore(); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x, pl.y, 58, 54, { isStatic: true, angle: a, friction: 0.4, restitution: 0.05, chamfer: { radius: 8 } }));
      b.kind = 'attic_magnet';
      const m = newMag(api, pl.x - Math.sin(a) * 27, pl.y + Math.cos(a) * 27, a);
      return {
        layer: 'mid',
        step(api) { magStep(api, m); },
        // the plain magnet never keeps the level running (it never lets go)
        busy() { return false; },
        draw(ctx, t) {
          field(ctx, m, t, m.on ? 1 : 0);
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); horseshoe(ctx); ctx.restore();
          for (const h of m.held) if (h.b.atticFrozen) sparkle(ctx, h.b.position.x + 14, h.b.position.y - 14, t, 0.9);
        },
      };
    },
    intro(ctx, t, g, h) {
      ctx.fillStyle = '#C99A6B'; ctx.fillRect(40, 200, 380, 16);
      const k = Math.min(1, Math.max(0, (t - 40) / 120)), e = h.ease ? h.ease(k) : k;
      const bx = 90 + e * 238;
      field(ctx, { x: 360, y: 184, nx: -1, ny: 0, range: 240 }, t, 1);
      ctx.save(); ctx.translate(387, 184); ctx.rotate(90 * DEG); horseshoe(ctx); ctx.restore();
      const fb = h.fakeBall(bx, 184, 3, 0, 16); fb.roll = bx / 16; fb.def = { skin: 'steel' } as Ball;
      g.ball(ctx, fb, 'hero', t);
      if (k >= 1 && t < 200) sparkle(ctx, bx + 12, 170, t, 1);
    },
  }),

  // Timer magnet: magnet with an egg timer. Holds the ball for a while – then the timer rings and the magnet lets go.
  // Tuned: hold 80 frames, stronger (S 9000, amax 1.3) and bigger catch distance than the plain magnet, so it grabs falling balls.
  attic_timer: definePart({
    rects: [{ x: 0, y: -6, w: 58, h: 66 }], rot: 45, defaultA: 0,
    draw(ctx, pl, sel) {
      const a = (pl.a || 0) * DEG, px = pl.x - Math.sin(a) * 27, py = pl.y + Math.cos(a) * 27;
      field(ctx, { x: px, y: py, nx: -Math.sin(a), ny: Math.cos(a), range: 230 }, performance.now() / 16, 0.8);
      if (sel) reach(ctx, px, py, 230);
      ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); horseshoe(ctx); ctx.restore();
      eggTimer(ctx, pl.x + Math.sin(a) * 24, pl.y - Math.cos(a) * 24, 0, 0);
      if (sel) { ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); selBox(ctx, 74, 90); ctx.restore(); }
    },
    icon(ctx) { ctx.save(); ctx.translate(0, 10); horseshoe(ctx, 1.15); ctx.restore(); eggTimer(ctx, 0, -30, 0.3, 0); },
    create(api, pl) {
      const a = (pl.a || 0) * DEG;
      const b = api.add(api.R(pl.x + Math.sin(a) * 6, pl.y - Math.cos(a) * 6, 58, 66, { isStatic: true, angle: a, friction: 0.4, restitution: 0.05, chamfer: { radius: 8 } }));
      b.kind = 'attic_timer';
      const m = newMag(api, pl.x - Math.sin(a) * 27, pl.y + Math.cos(a) * 27, a, { hold: 80, range: 240, catchD: 34, amax: 1.3, S: 9000 });
      return {
        layer: 'mid',
        step(api) {
          const was = m.on; magStep(api, m);
          const h = m.held[0];
          if (h && h.b.atticFrozen && h.t % 16 === 1) api.sfx('attic_tick', m.x, m.y);
          if (was && !m.on) api.sfx('attic_ding', m.x, m.y, '#FFE07A', 6);
        },
        busy() { return magBusy(m); },
        draw(ctx, t, g, S) {
          field(ctx, m, t, m.on ? 1 : 0);
          const h = m.held[0], k = h && h.b.atticFrozen ? h.t / m.hold : (m.on ? 0 : 1);
          const ring = !m.on && m.offT >= 0 && S.t - m.offT < 40 ? S.t - m.offT : 0;
          ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); horseshoe(ctx); ctx.restore();
          eggTimer(ctx, pl.x + Math.sin(a) * 24, pl.y - Math.cos(a) * 24, k, ring);
          if (h && h.b.atticFrozen) sparkle(ctx, h.b.position.x + 14, h.b.position.y - 14, t, 0.9);
        },
      };
    },
    intro(ctx, t, g, h) {
      // the ball rolls over the edge, the magnet catches it, the timer rings, the ball drops into the tin
      ctx.fillStyle = '#C99A6B'; ctx.fillRect(20, 150, 150, 14);
      ctx.save(); ctx.translate(300, 60); horseshoe(ctx); ctx.restore();
      let bx, by, k = 0;
      if (t < 50) { bx = 60 + t * 2.2; by = 134; }
      else if (t < 80) { const u = (t - 50) / 30; bx = 170 + u * 130; by = 134 - Math.sin(u * Math.PI / 2) * 30 + u * -1; }
      else if (t < 170) { bx = 300; by = 103; k = (t - 80) / 90; }
      else { bx = 300; by = Math.min(236, 103 + (t - 170) * (t - 170) * 0.09); k = 1; }
      eggTimer(ctx, 300, 30, k, t > 170 && t < 200 ? t - 170 : 0);
      if (t >= 80 && t < 170) field(ctx, { x: 300, y: 87, nx: 0, ny: 1, range: 120 }, t, 1);
      ctx.fillStyle = '#4A7FD0'; ctx.fillRect(250, 210, 100, 44); ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.strokeRect(250, 210, 100, 44);
      const fb = h.fakeBall(bx, by, 0, 0, 16); fb.def = { skin: 'steel' } as Ball; g.ball(ctx, fb, 'hero', t);
    },
  }),

  // Granny's iron: heavy, falls – and it is made of iron, so the magnet can hold it
  attic_iron: definePart({
    rects: [{ x: 0, y: 0, w: 70, h: 34 }], rot: false,
    draw(ctx, pl, sel) { iron(ctx, pl.x, pl.y, 0, sel); },
    icon(ctx) { iron(ctx, 0, 6, 0, false); },
    blockers(pl) { return [{ x: pl.x, y: pl.y, r: 40 }]; },
    create(api, pl) {
      const b = api.add(api.R(pl.x, pl.y, 70, 34, { density: 0.012, friction: 0.3, restitution: 0.05, frictionAir: 0.002, chamfer: { radius: 8 } }));
      b.kind = 'attic_iron'; b.atticIron = true; api.dynamic(b);
      api.onHit(b, o => { if (b.speed > 4 && !o.isSensor) api.emit('thunk', b.position.x, b.position.y, { v: b.speed }); });
      return { layer: 'mid', draw(ctx) { iron(ctx, b.position.x, b.position.y, b.angle, false); } };
    },
  }),
};

// ---------- objects ----------
/** Level data of attic_holder: act id (o.id) switches it off, timer = frames before it lets go */
interface HolderData extends ObjData { a?: number; id?: string; catchD?: number; timer?: number; S?: number; range?: number }
interface HolderSt { m: Magnet; a: number; slide: number }

const objs = {
  // Magnet behind a board / under a shelf. Holds the steel ball (CLICK). Its act id switches it off (it slides away), timer = frames before it lets go.
  attic_holder: defineObj<HolderSt>({
    layer: 'mid',
    create(api, data) {
      const o = data as HolderData;
      const a = (o.a || 0) * DEG;
      const m = newMag(api, o.x - Math.sin(a) * 27, o.y + Math.cos(a) * 27, a, { catchD: o.catchD || 26, hold: o.timer || 0, snap: false, S: o.S || 4300, range: o.range || 240 });
      const st = { m, a, slide: 0 };
      if (o.id) api.onAct(o.id, () => { if (!m.on) return; if (m.held.length) m.release = true; else { m.on = false; m.offT = api.S.t; api.sfx('attic_release', m.x, m.y); } });
      return st;
    },
    step(api, st) {
      const m = st.m;
      magStep(api, m);
      if (!m.on && st.slide < 1) st.slide = Math.min(1, st.slide + 0.06);
    },
    busy(api, st) { return st.m.held.some(h => !h.b.atticFrozen) || !!(st.m.hold && st.m.held.length > 0) || (!st.m.on && st.slide < 1); },
    blockers(o) { return [{ x: o.x, y: o.y, r: 36 }]; },
    draw(ctx, st, t) {
      const o = st.o, m = st.m, a = st.a, back = st.slide * 60;
      field(ctx, m, t, m.on ? 1 : 0);
      ctx.save(); ctx.translate(o.x + Math.sin(a) * back, o.y - Math.cos(a) * back); ctx.rotate(a);
      ctx.globalAlpha = 1 - st.slide * 0.5; horseshoe(ctx); ctx.restore();
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  attic_click: (tone, noise) => { tone(2400, 0.05, 'square', 0.12, 1800); tone(1300, 0.09, 'triangle', 0.22, 900, 0.02); noise(0.03, 0.15, 4000, 2); },
  attic_release: (tone) => { tone(700, 0.12, 'sine', 0.2, 300); },
  attic_ding: (tone) => { tone(1568, 0.5, 'sine', 0.22); tone(2093, 0.4, 'sine', 0.12, null, 0.05); },
  attic_tick: (tone) => { tone(3000, 0.02, 'square', 0.05); },
};

// ---------- reskins ----------
const reskin: WorldDef['reskin'] = {
  // old folding rule / ruler (outline colour is unused: block only checks it is set and draws its own yellow)
  plank(ctx, pl, sel, g, rects) {
    const r = rects[0];
    g.block(ctx, r.x, r.y, r.w, r.h, r.a, '#FFE58A', '#E9B53A', 5, { outline: sel ? '#FFC93C' : null });
    ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
    ctx.strokeStyle = 'rgba(90,60,20,.6)'; ctx.lineWidth = 2;
    for (let x = -r.w / 2 + 10; x < r.w / 2 - 4; x += 10) { ctx.beginPath(); ctx.moveTo(x, -r.h / 2 + 1); ctx.lineTo(x, -r.h / 2 + ((x / 10) % 5 ? 5 : 9)); ctx.stroke(); }
    ctx.restore();
  },
  // spring pad: pincushion
  pad(ctx, pl, sel, g, rects) { const r = rects[0]; pincushion(ctx, r.x, r.y, r.a, r.w, pl.squash || 0, sel); },
  // seesaw: ruler on a cotton reel
  seesaw(ctx, pl, sel, g) {
    const body = pl.body as GameBody | undefined;
    const x = pl.x, y = pl.y, a = body ? body.angle : (pl.a || 0) * DEG;
    spool(ctx, x, y);
    g.block(ctx, x, y, 230, 14, a, '#FFE58A', '#E9B53A', 6, { outline: sel ? '#FFC93C' : null });
    ctx.save(); ctx.translate(x, y); ctx.rotate(a); ctx.strokeStyle = 'rgba(90,60,20,.6)'; ctx.lineWidth = 2;
    for (let k = -105; k <= 105; k += 15) { ctx.beginPath(); ctx.moveTo(k, -7); ctx.lineTo(k, k % 45 ? -3 : 1); ctx.stroke(); }
    ctx.restore();
    ctx.beginPath(); ctx.arc(x, y, 6, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  },
  // sandbag (heavy, NOT iron)
  stone(ctx, pl, sel) { const body = pl.body as GameBody | undefined; sandbag(ctx, pl.x, pl.y, body ? body.angle : 0, sel); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { rulerIcon(ctx, g); },
  stone(ctx) { sandbag(ctx, 0, 0, 0, false); },
  pad(ctx) { pincushion(ctx, 0, 4, 0, 86, 0, false); },
};

// ---------- meta ----------
const meta: WorldMeta = {
  icon: '📻', strings, music: 'attic.mp3',
  credits: { title: 'Sunshine in My Teacup', artist: 'LokenWarrior', id: 502811, url: 'https://pixabay.com/music/traditional-jazz-sunshine-in-my-teacup-502811/' },
  synth: { bpm: 70, root: 196.0, sc: 'major', prog: [0, 5, 1, 4], bar: 4, lead: 'soft', dens: 0.4, sev: true },
};

export default defineWorld({
  k: K, meta, scene,
  wash: 'rgba(255,246,232,.6)', bgExtra,
  skins, ball: drawBall, goal,
  parts, objs, sounds, reskin, icons,
  levels,
});
